// How the Blog Hub's pageviews chart (blog_chart_controller.js) turns a daily
// series into the points it draws. Up to BY_DAY days showing, a point per
// day; up to BY_WEEK, a point per week; past that, a point per month, so
// years of GA history stay a readable line and every point is a real total.

export const BY_DAY = 120;
export const BY_WEEK = 730;

// What one point stands for, for `days` rows showing. A series that is
// already monthly stays monthly.
export function pointUnit(days, seriesUnit = "day") {
  if (seriesUnit === "month") return "month";
  if (days <= BY_DAY) return "day";
  return days <= BY_WEEK ? "week" : "month";
}

function parseDay(iso) {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, m - 1, d);
}

// The Monday a day's week starts on, as an ISO date.
export function weekStart(iso) {
  const date = parseDay(iso);
  date.setDate(date.getDate() - ((date.getDay() + 6) % 7));
  const pad = (n) => `${n}`.padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
    date.getDate()
  )}`;
}

// rows ([[iso, views], ...], oldest first) grouped into points:
// {label, first, last, from, to, views, complete}. label and first are the first day
// the point covers, last its last, and from/to its rows' indexes, so a drag
// across points maps back to days. A series that is already monthly
// (seriesUnit "month") is a row per whole month. Points at the edges of the window cover
// only the days showing.
export function chartPoints(rows, unit, seriesUnit = "day") {
  const keyOf = {
    day: (iso) => iso,
    week: weekStart,
    month: (iso) => iso.slice(0, 7),
  }[unit];
  const points = [];
  rows.forEach(([iso, views], i) => {
    const key = keyOf(iso);
    const last = points[points.length - 1];
    if (last && last.key === key) {
      last.views += views;
      last.last = iso;
      last.to = i;
    } else {
      points.push({
        key,
        label: iso,
        first: iso,
        last: iso,
        from: i,
        to: i,
        views,
      });
    }
  });
  points.forEach((point) => {
    point.complete = seriesUnit === "month" || covers(point, unit);
  });
  return points;
}

// Whether a point covers its whole week or month. The window's first and last
// points often don't (the month in progress), and the chart says so rather
// than letting a part-month read as a drop.
function covers(point, unit) {
  if (unit === "day") return true;
  const last = parseDay(point.last);
  const next = new Date(
    last.getFullYear(),
    last.getMonth(),
    last.getDate() + 1
  );
  if (unit === "week")
    return weekStart(point.first) === point.first && next.getDay() === 1;
  return point.first.endsWith("-01") && next.getDate() === 1;
}
